// src/admin/pages/audit/AuditLogTable.tsx
import React from 'react';
import {
  Avatar,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AuditLog } from './auditService';

interface AuditLogTableProps {
  logs: AuditLog[];
  isDark?: boolean;
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828' },
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a' },
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    textTransform: 'capitalize' as const,
    '& .MuiChip-label': { color: p.text },
  };
};

const ACTION_CHIP_COLOR: Record<string, string> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  status_change: 'warning',
  login: 'default',
  other: 'default',
};

const AuditLogTable: React.FC<AuditLogTableProps> = ({ logs, isDark = false }) => {
  const formatDate = (d: any) => {
    const date = d instanceof Date ? d : d?.toDate?.() || new Date(d);
    return date.toLocaleString();
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 900 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Module</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {logs.map((log) => (
            <TableRow
              key={log.id}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                '&:last-child td, &:last-child th': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(log.createdAt)}
                </Typography>
              </TableCell>

              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                    {(log.userName || '?')[0]?.toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">{log.userName || 'Unknown'}</Typography>
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {log.module}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  size="small"
                  label={log.action.replace('_', ' ')}
                  variant="outlined"
                  sx={getSoftChipSx(ACTION_CHIP_COLOR[log.action] || 'default', isDark)}
                />
              </TableCell>

              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                  {log.description}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AuditLogTable;
